/**
 * GereserveerdMateriaalLijst — status-gedreven paklijst (Stitch design)
 * Per categorie een kaart met voortgang; materialen als status-rijen.
 * Status: Gepakt / Nog pakken (exclusief) + Ingehuurd (los). Legacy-statussen
 * worden verborgen maar bij opslaan in de data behouden.
 */
import { React, useState, useEffect } from 'react';
import { groupBy } from 'lodash';
import { Box, Typography, Tooltip, Grid, Popover } from '@mui/material';
import MaterialItem from './MaterialItem';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  gold: '#d4943a',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const CATEGORY_ICONS = {
  'Cameras': 'videocam',
  'Drones': 'helicopter',
  'Lens': 'camera',
  'Monitors': 'monitor',
  'Statief': 'straighten',
  'Geluid': 'mic',
  'Grip': 'handyman',
  'Licht': 'lightbulb',
  'Camera Accessoires': 'settings_input_component',
  'Special effects': 'auto_awesome',
  'Studio equipment': 'storefront',
  'Vervoer en karren': 'local_shipping',
};
const CATEGORIES = Object.keys(CATEGORY_ICONS);

// Beheerde statussen (de rest is legacy → verborgen, maar behouden in de data)
const PRIMARY_OPTIONS = [
  { label: 'Gepakt', color: T.emerald },
  { label: 'Nog pakken', color: T.error },
];
const INGEHUURD = 'Ingehuurd';
const MANAGED_SET = new Set(['Gepakt', 'Nog pakken', 'Ingehuurd']);

const GereserveerdMateriaal = ({ reservedMaterials, setCurrentStepCategorie, setOpenWizard, updateJunctionStatus, onRemoveMaterial, onRestoreMaterial, typeById = {}, onOpenTypeInfo }) => {
  const [materials, setMaterials] = useState(reservedMaterials || []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverMaterialId, setPopoverMaterialId] = useState(null);

  useEffect(() => { setMaterials(reservedMaterials || []); }, [reservedMaterials]);
  // Ctrl+Z / Cmd+Z buiten tekstvelden = laatste actie terugdraaien
  useEffect(() => registreerOngedaanSneltoets(), []);

  const groupedMaterials = groupBy(materials, (m) => m.fields.Categorie);

  // Persisteer: behoud legacy-waarden, vervang alleen de beheerde statussen
  const persistStatuses = async (material, newManaged, { ongedaanTekst = null } = {}) => {
    const huidig = material.fields.JunctionStatus || [];
    const legacy = huidig.filter(s => !MANAGED_SET.has(s));
    const vorigeManaged = huidig.filter(s => MANAGED_SET.has(s));
    const newArr = [...legacy, ...newManaged];
    try {
      const response = await updateJunctionStatus(material, newArr);
      if (response && response.ok) {
        const bijgewerkt = { ...material, fields: { ...material.fields, JunctionStatus: newArr } };
        setMaterials(prev => prev.map(m => m.id === material.id ? bijgewerkt : m));
        if (ongedaanTekst) {
          // Ongedaan maken = dezelfde statuscall met de vorige beheerde statussen
          biedOngedaanAan(ongedaanTekst, async () => {
            const r = await updateJunctionStatus(bijgewerkt, [...legacy, ...vorigeManaged]);
            if (!r || !r.ok) throw new Error('Status terugzetten mislukt');
            setMaterials(prev => prev.map(m => m.id === material.id ? { ...m, fields: { ...m.fields, JunctionStatus: [...legacy, ...vorigeManaged] } } : m));
          });
        }
      }
    } catch (error) { console.error('Error saving statuses:', error); }
  };

  // Rij-klik / check: toggle Gepakt (verwijdert Nog pakken), behoud Ingehuurd + legacy
  const handleToggleGepakt = (material) => {
    const cur = material.fields.JunctionStatus || [];
    const ingehuurd = cur.includes(INGEHUURD) ? [INGEHUURD] : [];
    const newManaged = cur.includes('Gepakt') ? [...ingehuurd] : ['Gepakt', ...ingehuurd];
    persistStatuses(material, newManaged, { ongedaanTekst: cur.includes('Gepakt') ? 'Gepakt afgevinkt' : 'Gepakt aangevinkt' });
  };

  // Popover: primaire status zetten (Gepakt | Nog pakken | geen)
  const setPrimary = (material, primary) => {
    const cur = material.fields.JunctionStatus || [];
    const ingehuurd = cur.includes(INGEHUURD) ? [INGEHUURD] : [];
    persistStatuses(material, [...(primary ? [primary] : []), ...ingehuurd], { ongedaanTekst: primary ? `Status: ${primary}` : 'Status gewist' });
  };

  // Popover: Ingehuurd onafhankelijk togglen
  const toggleIngehuurd = (material) => {
    const cur = material.fields.JunctionStatus || [];
    const primary = cur.includes('Gepakt') ? ['Gepakt'] : cur.includes('Nog pakken') ? ['Nog pakken'] : [];
    persistStatuses(material, cur.includes(INGEHUURD) ? primary : [...primary, INGEHUURD], { ongedaanTekst: cur.includes(INGEHUURD) ? 'Ingehuurd uitgezet' : 'Ingehuurd aangezet' });
  };

  const handleOpenStatus = (e, material) => { setAnchorEl(e.currentTarget); setPopoverMaterialId(material.id); };
  const handleCloseStatus = () => { setAnchorEl(null); setPopoverMaterialId(null); };

  // Verwijderen uit reservering (optimistisch; parent hersaved de materiaallijst)
  const handleRemove = async (material) => {
    const index = materials.findIndex(m => m.id === material.id);
    setMaterials(prev => prev.filter(m => m.id !== material.id));
    try {
      await onRemoveMaterial?.(material);
      if (typeof onRestoreMaterial === 'function') {
        // Ongedaan maken = item via dezelfde opslagroute terugzetten (parent hersaved de lijst)
        biedOngedaanAan(`${material.fields?.ID || 'Materiaal'} uit reservering gehaald`, async () => {
          setMaterials(prev => {
            if (prev.some(m => m.id === material.id)) return prev;
            const copy = [...prev];
            copy.splice(Math.min(index, copy.length), 0, material);
            return copy;
          });
          await onRestoreMaterial(material);
        });
      }
    } catch (error) { console.error('Error removing material:', error); }
  };

  const handleOpenWizardToCategory = (category) => { setOpenWizard(true); setCurrentStepCategorie(CATEGORIES.indexOf(category)); };

  const pm = materials.find(m => m.id === popoverMaterialId);
  const pmStatuses = pm?.fields?.JunctionStatus || [];

  const lijstIsLeeg = !materials || materials.length === 0;

  return (
    <Box sx={{ mt: 1.5 }}>
      {/* Lege lijst: zonder deze kaart was er nérgens een toevoegen-knop —
          "ik kan geen materiaal toevoegen als er nog geen item staat"
          (wiki-feedback Robin, 1 sep). */}
      {lijstIsLeeg && (
        <Box onClick={() => setOpenWizard(true)} sx={{
          bgcolor: T.surface, borderRadius: '0.75rem', p: 3, mb: 2, boxShadow: T.shadow,
          display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
          border: `1px dashed ${T.outline}`, '&:hover': { bgcolor: T.surfaceLow },
        }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '0.5rem', bgcolor: T.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="add" size={20} sx={{ color: T.primary }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 700, color: T.text }}>
              Materiaal toevoegen
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline }}>
              Er staat nog niets op de lijst — begin met de eerste categorie.
            </Typography>
          </Box>
        </Box>
      )}
      {CATEGORIES.map((category) => {
        const materialsInCategory = groupedMaterials[category];
        if (!materialsInCategory || materialsInCategory.length === 0) return null;
        const total = materialsInCategory.length;
        const gepakt = materialsInCategory.filter(m => (m.fields.JunctionStatus || []).includes('Gepakt')).length;
        const pct = total ? Math.round((gepakt / total) * 100) : 0;
        const allDone = gepakt === total;
        return (
          <Box key={category} sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2, mb: 2, boxShadow: T.shadow }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '0.5rem', bgcolor: T.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={CATEGORY_ICONS[category] || 'inventory_2'} size={18} sx={{ color: T.primary }} />
                </Box>
                <Typography sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {category}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
                {/* Voortgang */}
                <Tooltip title={`${gepakt} van ${total} gepakt`} arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65 }}>
                    <Box sx={{ width: 52, height: 5, borderRadius: '9999px', bgcolor: T.surfaceHigh, overflow: 'hidden' }}>
                      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: T.emerald, borderRadius: '9999px', transition: 'width 0.3s ease' }} />
                    </Box>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 800, color: allDone ? T.emerald : T.outline, whiteSpace: 'nowrap' }}>
                      {gepakt}/{total}
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title={`Materiaal toevoegen aan ${category}`} arrow>
                  <Box onClick={() => handleOpenWizardToCategory(category)} sx={{
                    width: 28, height: 28, borderRadius: '0.35rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: T.surfaceLow },
                  }}>
                    <Icon name="add" size={18} sx={{ color: T.outline }} />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
            <Grid container spacing={1}>
              {materialsInCategory.map((material) => (
                <Grid item xs={12} sm={6} key={material.id}>
                  <MaterialItem
                    material={material}
                    onToggleGepakt={handleToggleGepakt}
                    onOpenStatus={handleOpenStatus}
                    onRemove={handleRemove}
                    typeById={typeById}
                    onOpenTypeInfo={onOpenTypeInfo}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      {/* Status-popover */}
      <Popover
        open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleCloseStatus}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: '0.75rem', mt: 0.5, boxShadow: '0 8px 30px rgba(45,51,53,0.15)', border: `1px solid ${T.surfaceHigh}`, overflow: 'visible' } }}
      >
        {pm && (
          <Box sx={{ p: 1.5, width: 232 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.64rem', fontWeight: 800, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.85 }}>
              {pm.fields?.ID || 'Materiaal'}
            </Typography>
            {/* Gepakt | Nog pakken (exclusief) */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
              {PRIMARY_OPTIONS.map(opt => {
                const active = pmStatuses.includes(opt.label);
                return (
                  <Box key={opt.label} onClick={() => setPrimary(pm, active ? null : opt.label)} sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4,
                    px: 1, py: 0.9, borderRadius: '0.5rem', cursor: 'pointer', transition: 'all 0.15s',
                    border: `1.5px solid ${active ? opt.color : T.surfaceHigh}`,
                    bgcolor: active ? `${opt.color}14` : T.surface,
                    '&:hover': { borderColor: opt.color },
                  }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: opt.color, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: active ? opt.color : T.secondary, whiteSpace: 'nowrap' }}>
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {/* Ingehuurd (los) */}
            <Box onClick={() => toggleIngehuurd(pm)} sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 1, py: 0.9, borderRadius: '0.5rem', cursor: 'pointer', transition: 'all 0.15s',
              border: `1.5px solid ${pmStatuses.includes(INGEHUURD) ? T.gold : T.surfaceHigh}`,
              bgcolor: pmStatuses.includes(INGEHUURD) ? `${T.gold}14` : T.surface,
              '&:hover': { borderColor: T.gold },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon name="local_shipping" size={15} sx={{ color: T.gold }} />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: pmStatuses.includes(INGEHUURD) ? T.gold : T.secondary }}>
                  Ingehuurd
                </Typography>
              </Box>
              {pmStatuses.includes(INGEHUURD) && <Icon name="check" size={15} sx={{ color: T.gold }} />}
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default GereserveerdMateriaal;
